import { useState, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '@/components/layout/AppLayout';
import { AdminGuard } from '@/components/guards/AdminGuard';
import { PageHeader } from '@/components/layout/PageHeader';
import { AuditLogTable } from '@/components/admin/AuditLogTable';
import { AuditLogFilters } from '@/components/admin/AuditLogFilters';
import { AuditLogDetail } from '@/components/admin/AuditLogDetail';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { Pagination } from '@/components/history/Pagination';
import { Toaster } from '@/components/ui/toaster';
import { auditLogApi, auditLogKeys } from '@/lib/api';
import type { AuditLogEntry, AuditEventType } from '@/types';

export const Route = createFileRoute('/admin/audit')({
  component: AdminAuditPage,
});

function AdminAuditPage() {
  const [eventType, setEventType] = useState<AuditEventType | 'all'>('all');
  const [userName, setUserName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

  const filters = useMemo(() => ({
    pageNumber: currentPage,
    pageSize: pageSize,
    eventType: eventType !== 'all' ? eventType : undefined,
  }), [currentPage, pageSize, eventType]);

  const {
    data: response,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: auditLogKeys.list(filters),
    queryFn: () => auditLogApi.list(filters),
  });

  // Filter by username client-side (server filter not available)
  const filteredEntries = useMemo(() => {
    if (!response?.items) return [];

    if (!userName) return response.items;

    return response.items.filter((entry) =>
      entry.userName?.toLowerCase().includes(userName.toLowerCase())
    );
  }, [response?.items, userName]);

  const totalCount = response?.totalCount ?? 0;

  const handleClearFilters = () => {
    setEventType('all');
    setUserName('');
    setCurrentPage(1);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleEventTypeChange = (value: AuditEventType | 'all') => {
    setEventType(value);
    setCurrentPage(1);
  };

  const handleUserNameChange = (value: string) => {
    setUserName(value);
  };

  return (
    <AdminGuard>
      <AppLayout>
        <div className="container max-w-6xl py-8">
          <PageHeader
            title="Audit Log"
            description="View system activity and security events"
          />

          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <LoadingSpinner size="lg" label="Loading audit log..." />
            </div>
          ) : isError ? (
            <ErrorMessage
              title="Failed to Load Audit Log"
              error={error}
              onRetry={() => refetch()}
            />
          ) : (
            <div className="space-y-4">
              <AuditLogFilters
                eventType={eventType}
                userName={userName}
                onEventTypeChange={handleEventTypeChange}
                onUserNameChange={handleUserNameChange}
                onClear={handleClearFilters}
              />

              <div className="text-sm text-muted-foreground">
                Showing {filteredEntries.length} of {response?.totalCount ?? 0} entries
              </div>

              <AuditLogTable
                entries={filteredEntries}
                onViewDetails={setSelectedEntry}
              />

              {totalCount > 0 && (
                <Pagination
                  page={currentPage}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
            </div>
          )}

          <AuditLogDetail
            open={!!selectedEntry}
            onOpenChange={(open) => !open && setSelectedEntry(null)}
            entry={selectedEntry}
          />

          <Toaster />
        </div>
      </AppLayout>
    </AdminGuard>
  );
}
